import './styles/variables.css';
import './styles/common.css';
import './styles/editor.css';
import './styles/preview.css';
import './styles/dashboard.css';

import { Nav } from './ui/common/Nav.js';
import { Footer } from './ui/common/Footer.js';
import { Toast } from './ui/common/Toast.js';
import { EditorPanel } from './ui/editor/EditorPanel.js';
import { ResumePreview } from './ui/preview/ResumePreview.js';
import { PNGExporter } from './export/PNGExporter.js';
import { PDFExporter } from './export/PDFExporter.js';
import { authAdapter } from './data/firebase/FirebaseAuthAdapter.js';
import { FirebaseResumeRepo } from './data/firebase/FirebaseResumeRepo.js';
import { FirebaseRaceEntryRepo } from './data/firebase/FirebaseRaceEntryRepo.js';
import { FirebaseImageRepo } from './data/firebase/FirebaseImageRepo.js';
import { Resume } from './domain/models/Resume.js';
import { RaceEntry } from './domain/models/RaceEntry.js';
import { StatsCalculator } from './domain/services/StatsCalculator.js';
import { PRDetector } from './domain/services/PRDetector.js';

// Initialize repositories
const resumeRepo = new FirebaseResumeRepo();
const raceRepo = new FirebaseRaceEntryRepo();
const imageRepo = new FirebaseImageRepo();

// App state
let currentUser = null;
let currentResume = null;
let debounceTimer = null;
let _isProcessing = false;

// Initialize components
const nav = new Nav({
  onExportPDF: handleExportPDF,
  onExportPNG: handleExportPNG,
  onPublishToggle: handlePublishToggle,
  onSignOut: handleSignOut,
  user: null,
});

const editorPanel = new EditorPanel({
  onProfileChange: handleProfileChange,
  onRaceAdd: handleRaceAdd,
  onRaceEdit: handleRaceEdit,
  onRaceDelete: handleRaceDelete,
  onRaceHighlightToggle: handleHighlightToggle,
  onTemplateChange: handleTemplateChange,
});

const preview = new ResumePreview();
const footer = new Footer();

// ---------------------------------------------------------------------------
// Render layout
// ---------------------------------------------------------------------------

function renderApp() {
  const app = document.getElementById('app');
  app.innerHTML = '';

  // Nav
  const navContainer = document.createElement('div');
  nav.render(navContainer);
  app.appendChild(navContainer);

  // Main content: split layout
  const main = document.createElement('main');
  main.className = 'app-layout';
  main.innerHTML = `
    <div class="editor-pane" id="editor-pane"></div>
    <div class="preview-pane" id="preview-pane"></div>
  `;
  app.appendChild(main);

  // Footer
  const footerContainer = document.createElement('div');
  footer.render(footerContainer);
  app.appendChild(footerContainer);

  // Render sub-components into panes
  editorPanel.render(document.getElementById('editor-pane'));
  preview.render(document.getElementById('preview-pane'));
}

// ---------------------------------------------------------------------------
// Auth state listener
// ---------------------------------------------------------------------------

async function initAuth() {
  // On mobile, wait for the redirect sign-in result before listening to auth state.
  // Without this, onAuthStateChanged fires with null (no user yet) before the
  // redirect completes, causing a flash of the sign-in screen.
  await authAdapter.waitForRedirect();

  authAdapter.onAuthStateChanged(async (user) => {
    currentUser = user;
    nav.updateUser(user);

    if (user) {
      await loadOrCreateResume(user.uid);
    } else {
      showSignInScreen();
    }
  });
}

// ---------------------------------------------------------------------------
// Show sign-in screen for unauthenticated users
// ---------------------------------------------------------------------------

function showSignInScreen() {
  const app = document.getElementById('app');
  // Replace the split layout with a full-width landing page
  const main = app.querySelector('.app-layout');
  if (main) main.remove();

  const landing = document.createElement('div');
  landing.className = 'landing';
  landing.innerHTML = `
    <section class="landing__hero">
      <h1 class="landing__title">
        Your races deserve<br>
        <span class="landing__highlight">more than</span> sitting in<br>
        Strava history.
      </h1>
      <p class="landing__sub">
        Build your complete race history — road or trail, big or small.<br>
        Generate a clean, beautiful resume you can share anywhere.
      </p>
      <button class="btn-sign-in" id="btn-google-signin">
        <svg width="20" height="20" viewBox="0 0 48 48">
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
        </svg>
        Sign in with Google — it's free
      </button>
    </section>

    <section class="landing__steps">
      <h2 class="sr-only">How It Works</h2>
      <div class="landing__step">
        <div class="landing__step-icon landing__step-icon--cyan">＋</div>
        <div class="landing__step-phase">PHASE 01</div>
        <h3 class="landing__step-title">Add your races</h3>
        <div class="landing__step-desc">Build your complete race history — road or trail, big or small.</div>
      </div>
      <div class="landing__step">
        <div class="landing__step-icon landing__step-icon--green">★</div>
        <div class="landing__step-phase">PHASE 02</div>
        <h3 class="landing__step-title">Mark highlights</h3>
        <div class="landing__step-desc">Select the races that define your journey — your PRs, ultras, and milestone finishes.</div>
      </div>
      <div class="landing__step">
        <div class="landing__step-icon landing__step-icon--gold">📄</div>
        <div class="landing__step-phase">PHASE 03</div>
        <h3 class="landing__step-title">Export resume</h3>
        <div class="landing__step-desc">Generate a clean, beautiful resume with high-end editorial layouts you can share anywhere.</div>
      </div>
      <div class="landing__step">
        <div class="landing__step-icon landing__step-icon--purple">🔗</div>
        <div class="landing__step-phase">PHASE 04</div>
        <h3 class="landing__step-title">Share with link</h3>
        <div class="landing__step-desc">Create your own public running resume link and showcase your journey with friends.</div>
      </div>
      <div class="landing__step">
        <div class="landing__step-icon landing__step-icon--cyan">📊</div>
        <div class="landing__step-phase">PHASE 05</div>
        <h3 class="landing__step-title">Race dashboard</h3>
        <div class="landing__step-desc">Visualize your running journey — distance trends, personal bests, race maps, achievement badges, and more.</div>
      </div>
    </section>
  `;

  // Insert before footer container (footer is inside a wrapper div)
  const footerEl = app.querySelector('footer');
  const footerContainer = footerEl?.parentElement;
  if (footerContainer && footerContainer.parentElement === app) {
    app.insertBefore(landing, footerContainer);
  } else {
    app.appendChild(landing);
  }

  document.getElementById('btn-google-signin')?.addEventListener('click', async () => {
    try {
      await authAdapter.signInWithGoogle();
    } catch (err) {
      console.error('Sign in failed:', err);
      Toast.show('Sign in failed. Please try again.', 'error');
    }
  });
}

// ---------------------------------------------------------------------------
// Load existing resume or create a new one
// ---------------------------------------------------------------------------

async function loadOrCreateResume(userId) {
  try {
    const app = document.getElementById('app');

    // Remove landing page if present
    const landing = app.querySelector('.landing');
    if (landing) landing.remove();

    // Ensure the split layout exists
    let main = app.querySelector('.app-layout');
    if (!main) {
      main = document.createElement('main');
      main.className = 'app-layout';
      main.innerHTML = `
        <div class="editor-pane" id="editor-pane"></div>
        <div class="preview-pane" id="preview-pane"></div>
      `;
      const footerEl = app.querySelector('footer');
      const footerWrap = footerEl?.parentElement;
      if (footerWrap && footerWrap.parentElement === app) {
        app.insertBefore(main, footerWrap);
      } else {
        app.appendChild(main);
      }
    }

    // Re-render editor/preview panes
    const editorPane = document.getElementById('editor-pane');
    const previewPane = document.getElementById('preview-pane');
    if (editorPane) {
      editorPane.innerHTML = '';
      editorPanel.render(editorPane);
    }
    if (previewPane) {
      previewPane.innerHTML = '';
      preview.render(previewPane);
    }

    let resume = null;

    // Try localStorage cached resume ID first
    const cachedResumeId = localStorage.getItem('resumeId');
    if (cachedResumeId) {
      resume = await resumeRepo.getById(cachedResumeId);
    }

    // Fall back to query by userId
    if (!resume) {
      resume = await resumeRepo.getByUserId(userId);
    }

    // Only create new if both return null
    if (!resume) {
      resume = new Resume({
        userId,
        name: currentUser.displayName || '',
        subtitle: '',
        profileImageUrl: currentUser.photoURL || null,
        templateId: 'classic',
        isPublished: false,
        races: [],
      });
      resume = await resumeRepo.create(resume);
    }

    // Cache resume ID on success
    if (resume?.id) {
      localStorage.setItem('resumeId', resume.id);
    }

    currentResume = resume;

    // Populate editor with resume data
    editorPanel.setResume(currentResume);
    nav.updateResume(currentResume);
    updatePreview();
  } catch (err) {
    console.error('Failed to load resume:', err);
    Toast.show('Failed to load your resume. Please refresh.', 'error');
  }
}

// ---------------------------------------------------------------------------
// Update live preview (debounced)
// ---------------------------------------------------------------------------

function updatePreview() {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    if (!currentResume) return;

    // Detect PRs (mutates race.isPR flags)
    PRDetector.detect(currentResume.races);

    // Calculate stats
    const stats = StatsCalculator.calculate(currentResume.races);

    preview.update({
      resume: currentResume,
      stats,
      templateId: currentResume.templateId,
    });
  }, 300);
}

// ---------------------------------------------------------------------------
// Event handlers
// ---------------------------------------------------------------------------

async function handleProfileChange({ name, subtitle, profileImageFile }) {
  if (!currentResume) return;

  try {
    const updates = {};
    if (name !== undefined) {
      currentResume.name = name;
      updates.name = name;
    }
    if (subtitle !== undefined) {
      currentResume.subtitle = subtitle;
      updates.subtitle = subtitle;
    }

    if (profileImageFile) {
      const imageUrl = await imageRepo.uploadProfileImage(currentUser.uid, profileImageFile);
      currentResume.profileImageUrl = imageUrl;
      updates.profileImageUrl = imageUrl;
    }

    if (Object.keys(updates).length > 0) {
      await resumeRepo.update(currentResume.id, updates);
    }

    updatePreview();
  } catch (err) {
    console.error('Failed to update profile:', err);
    Toast.show('Failed to save changes.', 'error');
  }
}

async function handleRaceAdd(raceData) {
  if (!currentResume || _isProcessing) return;

  _isProcessing = true;
  try {
    const validation = RaceEntry.validate(raceData);
    if (!validation.valid) {
      Toast.show(validation.errors[0], 'error');
      _isProcessing = false;
      return;
    }

    const race = new RaceEntry(raceData);
    const savedRace = await raceRepo.add(currentResume.id, race);
    // Use the saved race which has the server-generated ID
    currentResume.races.push(savedRace);

    // Re-detect PRs after adding
    PRDetector.detect(currentResume.races);

    editorPanel.setResume(currentResume);
    updatePreview();
    Toast.show('Race added!', 'success');
  } catch (err) {
    console.error('Failed to add race:', err);
    Toast.show('Failed to add race.', 'error');
  } finally {
    _isProcessing = false;
  }
}

async function handleRaceEdit(raceData) {
  // EditorPanel calls onRaceEdit with the full data object including .id
  if (!currentResume) return;

  const raceId = raceData.id;
  if (!raceId) return;

  try {
    await raceRepo.update(currentResume.id, raceId, raceData);

    const index = currentResume.races.findIndex((r) => r.id === raceId);
    if (index >= 0) {
      Object.assign(currentResume.races[index], raceData);
    }

    PRDetector.detect(currentResume.races);
    editorPanel.setResume(currentResume);
    updatePreview();
    Toast.show('Race updated!', 'success');
  } catch (err) {
    console.error('Failed to update race:', err);
    Toast.show('Failed to update race.', 'error');
  }
}

async function handleRaceDelete(raceId) {
  if (!currentResume) return;

  try {
    await raceRepo.delete(currentResume.id, raceId);
    currentResume.races = currentResume.races.filter((r) => r.id !== raceId);

    PRDetector.detect(currentResume.races);
    editorPanel.setResume(currentResume);
    updatePreview();
    Toast.show('Race deleted.', 'info');
  } catch (err) {
    console.error('Failed to delete race:', err);
    Toast.show('Failed to delete race.', 'error');
  }
}

async function handleHighlightToggle(raceId) {
  if (!currentResume) return;

  const race = currentResume.races.find((r) => r.id === raceId);
  if (race) {
    race.isHighlighted = !race.isHighlighted;
    try {
      await raceRepo.update(currentResume.id, raceId, { isHighlighted: race.isHighlighted });
    } catch (err) {
      console.error('Failed to toggle highlight:', err);
      // Revert on failure
      race.isHighlighted = !race.isHighlighted;
      Toast.show('Failed to update highlight.', 'error');
      return;
    }
    editorPanel.setResume(currentResume);
    updatePreview();
  }
}

async function handleTemplateChange(templateId) {
  if (!currentResume) return;

  currentResume.templateId = templateId;
  try {
    await resumeRepo.update(currentResume.id, { templateId });
  } catch (err) {
    console.error('Failed to save template:', err);
    Toast.show('Failed to save template choice.', 'error');
  }
  updatePreview();
}

async function handlePublishToggle() {
  if (!currentResume || _isProcessing) return;

  _isProcessing = true;
  try {
    const newState = !currentResume.isPublished;
    await resumeRepo.update(currentResume.id, { isPublished: newState });
    currentResume.isPublished = newState;
    editorPanel.setResume(currentResume);
    nav.updateResume(currentResume);
    updatePreview();
    Toast.show(newState ? 'Resume published!' : 'Resume unpublished.', newState ? 'success' : 'info');
  } catch (err) {
    console.error('Failed to toggle publish:', err);
    Toast.show('Failed to update publish status.', 'error');
  } finally {
    _isProcessing = false;
  }
}

async function handleExportPDF() {
  if (!currentResume || _isProcessing) return;

  _isProcessing = true;
  try {
    const element = preview.getExportElement();
    if (!element) {
      Toast.show('Nothing to export.', 'error');
      return;
    }

    const name = (currentResume.name || 'resume').replace(/\s+/g, '-').toLowerCase();

    await PDFExporter.export(element, {
      fileName: `runners-resume-${name}.pdf`,
    });

    Toast.show('PDF exported!', 'success');
  } catch (err) {
    console.error('PDF export failed:', err);
    Toast.show('PDF export failed. Please try again.', 'error');
  } finally {
    _isProcessing = false;
  }
}

async function handleExportPNG() {
  if (!currentResume || _isProcessing) return;

  _isProcessing = true;
  try {
    const element = preview.getExportElement();
    if (!element) {
      Toast.show('Nothing to export.', 'error');
      return;
    }

    const shareUrl = currentResume.isPublished
      ? `https://therunninggenie.com/r/${currentResume.id}`
      : null;

    const name = (currentResume.name || 'resume').replace(/\s+/g, '-').toLowerCase();

    await PNGExporter.export(element, {
      fileName: `runners-resume-${name}.png`,
      shareUrl,
    });

    Toast.show('PNG exported!', 'success');
  } catch (err) {
    console.error('PNG export failed:', err);
    Toast.show('Export failed. Please try again.', 'error');
  } finally {
    _isProcessing = false;
  }
}

async function handleSignOut() {
  try {
    await authAdapter.signOut();
    currentResume = null;
    Toast.show('Signed out.', 'info');
  } catch (err) {
    console.error('Sign out failed:', err);
    Toast.show('Sign out failed.', 'error');
  }
}

// ---------------------------------------------------------------------------
// Boot the app
// ---------------------------------------------------------------------------

// Only boot the editor app if #app exists (share page has #share-app instead)
if (document.getElementById('app')) {
  renderApp();
  initAuth();
}
